/**
 * Law Library — the regulatory-currency engine, made plain.
 *
 * State breach-notification laws change. This is where the privacy officer keeps
 * the org's copy current: paste the latest statute or regulator page, the agent
 * drafts the structured rule, and the officer reviews and activates it. Every
 * version is kept with an "effective-from" date, so a case is always assessed
 * under the law that was in force when it happened.
 *
 * The agent drafts only from the source you paste — it never invents law, and
 * nothing it drafts takes effect until a privacy officer activates it.
 */

import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'], ['FL', 'Florida'],
  ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'],
  ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'],
  ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming']
];

const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  draft: 'bg-amber-50 text-amber-700 border border-amber-200',
  superseded: 'bg-slate-100 text-slate-500 border border-slate-200'
};

const FIELDS = [
  ['individual_deadline_days', 'Days to notify individuals', 'number', 'Days after determination the state allows to notify affected residents. Federal HIPAA is 60; a shorter state deadline governs.'],
  ['ag_notification_required', 'Notify state Attorney General', 'bool', 'Whether the state requires notice to its Attorney General or regulator.'],
  ['ag_deadline_days', 'Days to notify AG', 'number', 'Deadline to notify the AG, if the state sets one.'],
  ['ag_threshold_residents', 'AG threshold (residents)', 'number', 'AG notice triggers only above this many affected residents, if the state sets a floor.'],
  ['media_threshold_residents', 'Media-notice threshold', 'number', 'Number of affected residents that triggers a media notice, if any.'],
  ['effective_from', 'Effective from', 'date', 'The date this version of the law took effect. Cases are assessed under the version in force on the case date.'],
  ['citation', 'Citation', 'text', 'The statute or regulation reference (e.g., Cal. Civ. Code § 1798.82).'],
  ['notes', 'Key nuances', 'textarea', 'Anything that changes how the rule applies — risk-of-harm standard, credit-monitoring requirement, how the state defines personal information.']
];

function Tip({ text }) {
  return (
    <span className="group relative inline-flex ml-1 align-middle">
      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-20 leading-snug pointer-events-none">
        {text}
      </span>
    </span>
  );
}

export default function PrivacyLawLibrary() {
  const [stateCode, setStateCode] = useState('CA');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);      // the in-review version row
  const [confidence, setConfidence] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const stateName = (STATES.find((s) => s[0] === stateCode) || [, stateCode])[1];

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/api/privacy/law/versions?state=${stateCode}`, { credentials: 'include' });
      const data = await res.json();
      setVersions(data.success ? (data.versions || []) : []);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [stateCode]);

  useEffect(() => { load(); setDraft(null); setConfidence(null); setSource(''); }, [load]);

  const handleDraft = async () => {
    if (!source.trim()) return;
    setDrafting(true); setErr('');
    try {
      const res = await fetch(`${API}/api/privacy/law/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state_code: stateCode, state_name: stateName, source_text: source })
      });
      const data = await res.json();
      if (data.success) { setDraft(data.version); setConfidence(data.confidence || null); load(); }
      else setErr(data.message || 'Could not draft the update.');
    } catch (e) {
      setErr(e.message || 'Could not reach the drafting service.');
    } finally {
      setDrafting(false);
    }
  };

  const saveDraftField = (id, value) => setDraft((d) => ({ ...d, [id]: value }));

  const persistDraft = async () => {
    if (!draft) return;
    setBusy(true); setErr('');
    try {
      const body = {};
      FIELDS.forEach(([id]) => { body[id] = draft[id] ?? null; });
      const res = await fetch(`${API}/api/privacy/law/versions/${draft.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) { setDraft(data.version); load(); }
      else setErr(data.message || 'Could not save.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const activate = async (id) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/privacy/law/versions/${id}/activate`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) { setDraft(null); setConfidence(null); setSource(''); load(); }
      else setErr(data.message || 'Could not activate. Activation is limited to the privacy officer.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const fmtVal = (id, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (id === 'ag_notification_required') return v ? 'Yes' : 'No';
    if (id === 'individual_deadline_days' || id === 'ag_deadline_days') return `${v} days`;
    return String(v);
  };

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900 tracking-tight">Law Library</h2>
          <p className="text-slate-600 text-sm mt-1 max-w-3xl">
            Keep your state breach-notification rules current. Paste the latest statute or regulator page and the
            agent drafts the structured rule for you to review. Nothing takes effect until you activate it, and every
            version is kept with an effective-from date — so a case is always assessed under the law in force at the time.
          </p>
        </div>

        {/* State picker */}
        <div className="flex items-center gap-3 mb-6">
          <label className="text-sm font-medium text-slate-700">State</label>
          <select
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value)}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>

        {err && (
          <div className="mb-5 rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-700">{err}</div>
        )}

        {/* Draft-from-source */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">
              Draft an update for {stateName}
              <Tip text="Paste the statute text, a regulator bulletin, or a reputable legal summary. The agent reads only what you paste — it won't invent anything." />
            </h3>
            <span className="text-xs text-slate-400">AI-assisted · you verify before it counts</span>
          </div>
          <textarea
            value={source}
            onChange={(e) => setSource(e.target.value)}
            rows={5}
            placeholder={`Paste the latest ${stateName} breach-notification source here…`}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={handleDraft}
              disabled={drafting || !source.trim()}
              className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 transition-all"
            >
              {drafting ? 'Reading the source…' : 'Draft with AI'}
            </button>
          </div>
        </div>

        {/* Draft review */}
        {draft && (
          <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-800">Review the draft</h3>
              <div className="flex items-center gap-2">
                {confidence && (
                  <span className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-600">Agent confidence: {confidence}</span>
                )}
                <span className={`text-xs px-2 py-1 rounded-lg ${STATUS_STYLE.draft}`}>Draft</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              The agent filled what the source stated and left the rest blank. Correct anything, then activate to make this the version of record.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(([id, label, type, tip]) => (
                <div key={id} className={type === 'textarea' ? 'md:col-span-2' : ''}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{label}<Tip text={tip} /></label>
                  {type === 'bool' ? (
                    <select
                      value={draft[id] === true ? 'yes' : draft[id] === false ? 'no' : ''}
                      onChange={(e) => saveDraftField(id, e.target.value === '' ? null : e.target.value === 'yes')}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not stated</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : type === 'textarea' ? (
                    <textarea
                      value={draft[id] || ''}
                      onChange={(e) => saveDraftField(id, e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <input
                      type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
                      value={draft[id] ?? ''}
                      onChange={(e) => saveDraftField(id, e.target.value === '' ? null : (type === 'number' ? Number(e.target.value) : e.target.value))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 mt-5">
              <button type="button" onClick={persistDraft} disabled={busy}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                Save edits
              </button>
              <button type="button" onClick={() => activate(draft.id)} disabled={busy}
                className="px-5 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium shadow-sm hover:bg-emerald-700 hover:shadow-lg disabled:opacity-50">
                {busy ? 'Working…' : 'Verify & activate'}
              </button>
            </div>
          </div>
        )}

        {/* Version history */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">{stateName} — versions on file</h3>
            <span className="text-xs text-slate-400">Most recent first</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : versions.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No versions yet for {stateName}. Paste a source above to draft the first one.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {versions.map((v) => (
                <div key={v.id} className="px-5 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-lg ${STATUS_STYLE[v.status] || STATUS_STYLE.superseded}`}>
                        {v.status === 'active' ? 'Active' : v.status === 'draft' ? 'Draft' : 'Superseded'}
                      </span>
                      {v.ai_drafted && <span className="text-[11px] text-slate-400">AI-drafted</span>}
                      {v.effective_from && <span className="text-[11px] text-slate-500">Effective {String(v.effective_from).slice(0, 10)}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      {v.verified_by_name && <span className="text-[11px] text-slate-400">Verified by {v.verified_by_name}</span>}
                      {v.status === 'draft' && (
                        <button type="button" onClick={() => activate(v.id)} disabled={busy}
                          className="text-xs text-emerald-700 font-medium hover:underline disabled:opacity-50">
                          Verify & activate
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                    {FIELDS.filter(([id]) => id !== 'notes' && id !== 'effective_from').map(([id, label]) => (
                      <div key={id} className="flex justify-between gap-2">
                        <span className="text-slate-500">{label}</span>
                        <span className="text-slate-800 font-medium text-right">{fmtVal(id, v[id])}</span>
                      </div>
                    ))}
                  </div>
                  {v.notes && <p className="mt-2 text-xs text-slate-600 leading-snug">{v.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
